/**
 * AdminTemplateGenerator.jsx
 *
 * AI Template Generator — Admin Panel
 *
 * Flow:
 *  1. Select Category + Subcategory
 *  2. Click Generate → backend returns N template_json objects (NOT saved)
 *  3. Admin previews cards; each card shows the actual scaled canvas
 *  4. Save individually per card OR "Save All" in one batch
 *  5. Regenerate / Delete per card at any time
 *
 * State rules:
 *  - `savedSet`  (useState<Set>) drives ALL "saved" UI — no useRef
 *  - TemplateCard receives `isSaved` prop computed from `savedSet`
 *  - Parent owns save logic; cards call onSave which updates savedSet
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import AdminLayout from './AdminLayout';
import { Button } from '../../components/ui/button';
import {
  Sparkles, CheckCircle2, AlertCircle, Loader2,
  ChevronDown, LayoutTemplate, RefreshCw, Trash2,
  Save, Zap, Info, Eye,
} from 'lucide-react';
import PageThumbnailPreview from '../../components/Editor/PageThumbnailPreview';
import { captureTemplateThumbnail } from '../../utils/captureTemplateThumbnail';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const authH = () => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${localStorage.getItem('admin_token')}`,
});

// ── Canvas preview wrapper ────────────────────────────────────────────────────
// Same renderer as the public template cards / editor — PageThumbnailPreview
// cover-scales into the card's fixed preview area.
const CardCanvasPreview = ({ templateJson, height = 155 }) => {
  const page = templateJson?.pages?.[0];
  const cw   = templateJson?.canvas_width  || 1080;
  const ch   = templateJson?.canvas_height || 1080;
  if (!page) {
    return <div className="rounded-t-xl flex-shrink-0" style={{ height, background: '#cbd5e1' }} />;
  }
  return (
    <div
      className="relative rounded-t-xl overflow-hidden flex-shrink-0"
      style={{ height, background: '#cbd5e1' }}
    >
      <PageThumbnailPreview
        page={page}
        canvasSize={{ width: cw, height: ch }}
        fillContainer
      />
    </div>
  );
};

// ── Single template card ──────────────────────────────────────────────────────
// isSaved comes from parent (savedSet) — NOT from local state.
// This ensures Save All / individual save are always in sync.

const TemplateCard = ({ item, isSaved, onSave, onDelete, onRegenerate, onThumbnailReady }) => {
  const [saving,    setSaving]    = useState(false);
  const [regenning, setRegenning] = useState(false);
  const [cardError, setCardError] = useState('');

  // Capture thumbnail via the shared pipeline — mounts PageThumbnailPreview
  // off-screen at canvas resolution and html2canvases it with the same
  // clip-path bridge the editor download uses. Stored thumbnails now match
  // every live render call site bit-for-bit.
  useEffect(() => {
    if (!onThumbnailReady || !item.template_json) return;
    let cancelled = false;
    const timer = setTimeout(async () => {
      const dataUrl = await captureTemplateThumbnail(item.template_json);
      if (cancelled || !dataUrl) return;
      console.log('[Thumbnail] captured for', item.client_id, dataUrl.length, 'bytes');
      onThumbnailReady(item.client_id, dataUrl);
    }, 200);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [item.client_id]); // eslint-disable-line react-hooks/exhaustive-deps

  const tj       = item.template_json;
  const cw       = tj?.canvas_width  || item.canvas_width  || 0;
  const ch       = tj?.canvas_height || item.canvas_height || 0;
  const hash     = item.uniqueness_hash || tj?.uniqueness_hash || '';
  const textEls  = (tj?.pages?.[0]?.elements || []).filter(e => e.type === 'text');
  const heading  = textEls[0]?.text ?? textEls[0]?.content ?? '';
  const subtitle = textEls[1]?.text ?? textEls[1]?.content ?? '';
  const elCount  = (tj?.pages?.[0]?.elements || []).length;

  const handleSave = async () => {
    setSaving(true);
    setCardError('');
    try {
      await onSave(item);
    } catch (e) {
      setCardError(e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleRegen = async () => {
    setRegenning(true);
    setCardError('');
    try {
      await onRegenerate(item.client_id);
    } finally {
      setRegenning(false);
    }
  };

  const busy = saving || regenning;

  return (
    <div className={`rounded-xl border overflow-hidden bg-white shadow-sm flex flex-col transition-all duration-200
      ${isSaved ? 'border-green-300 ring-1 ring-green-200 shadow-green-100/60'
                : 'border-gray-200 hover:border-gray-300 hover:shadow-md'}`}
    >
      <CardCanvasPreview templateJson={tj} />

      <div className="p-3 flex flex-col gap-2 flex-1">
        {/* Heading + subtitle */}
        <div>
          <p className="text-xs font-semibold text-gray-800 truncate leading-snug">
            {heading || '(no heading)'}
          </p>
          {subtitle && (
            <p className="text-[10px] text-gray-400 truncate mt-0.5">{subtitle}</p>
          )}
        </div>

        {/* Meta row */}
        <div className="flex items-center justify-between gap-1">
          <span className="text-[10px] text-gray-400 font-mono">{cw}×{ch}</span>
          <span className="text-[10px] text-gray-300">{elCount} els</span>
        </div>

        {/* Hash chip */}
        {hash && (
          <span className="text-[9px] text-gray-300 font-mono truncate">#{hash}</span>
        )}

        {/* Category / subcategory chips */}
        <div className="flex gap-1 flex-wrap">
          {item.category && (
            <span className="text-[9px] bg-violet-50 text-violet-600 border border-violet-100 rounded px-1.5 py-0.5 truncate">
              {item.category}
            </span>
          )}
          {item.subcategory && (
            <span className="text-[9px] bg-sky-50 text-sky-600 border border-sky-100 rounded px-1.5 py-0.5 truncate">
              {item.subcategory}
            </span>
          )}
        </div>

        {/* Error */}
        {cardError && (
          <p className="text-[10px] text-red-500 flex items-center gap-1 leading-tight">
            <AlertCircle className="w-3 h-3 flex-shrink-0" />
            <span className="truncate">{cardError}</span>
          </p>
        )}

        {/* Action row */}
        {isSaved ? (
          <div className="flex items-center gap-1 text-xs text-green-600 font-medium pt-0.5">
            <CheckCircle2 className="w-3.5 h-3.5" />
            Saved
          </div>
        ) : (
          <div className="flex items-center gap-1 pt-0.5">
            <Button
              size="sm"
              onClick={handleSave}
              disabled={busy}
              className="flex-1 h-7 text-xs bg-violet-600 hover:bg-violet-700 text-white"
            >
              {saving
                ? <Loader2 className="w-3 h-3 animate-spin" />
                : <><Save className="w-3 h-3 mr-1" />Save</>}
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={handleRegen}
              disabled={busy}
              className="h-7 w-7 p-0 flex-shrink-0"
              title="Regenerate"
            >
              {regenning
                ? <Loader2 className="w-3 h-3 animate-spin" />
                : <RefreshCw className="w-3 h-3" />}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => onDelete(item.client_id)}
              disabled={busy}
              className="h-7 w-7 p-0 flex-shrink-0 hover:bg-red-50 hover:text-red-500"
              title="Remove"
            >
              <Trash2 className="w-3 h-3" />
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

// ── Dropdown wrapper ──────────────────────────────────────────────────────────
const DropDown = ({ value, onChange, disabled, placeholder, children }) => (
  <div className="relative">
    <select
      value={value}
      onChange={onChange}
      disabled={disabled}
      className="w-full appearance-none border border-gray-300 rounded-lg px-3 py-2.5 pr-9 text-sm
        bg-white focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent
        disabled:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed"
    >
      {placeholder && <option value="">{placeholder}</option>}
      {children}
    </select>
    <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
  </div>
);

// ── Checkbox row ──────────────────────────────────────────────────────────────
const Check = ({ checked, onChange, label }) => (
  <label className="flex items-center gap-2 cursor-pointer select-none">
    <input
      type="checkbox"
      checked={checked}
      onChange={onChange}
      className="w-4 h-4 accent-violet-600 rounded"
    />
    <span className="text-sm text-gray-700">{label}</span>
  </label>
);

// ── Main component ────────────────────────────────────────────────────────────
const AdminTemplateGenerator = () => {

  // ── Settings ───────────────────────────────────────────────────────────────
  const [categories,  setCategories]  = useState([]);  // [{id, name, canvas, subcategories}]
  const [category,    setCategory]    = useState('');
  const [subcategory, setSubcategory] = useState('');
  const [count,       setCount]       = useState(10);
  const [deduplicate, setDeduplicate] = useState(true);
  const [autoPublish, setAutoPublish] = useState(false);

  // ── Preview list ───────────────────────────────────────────────────────────
  const [previews,    setPreviews]    = useState([]);  // envelope objects from backend
  const [savedSet,    setSavedSet]    = useState(() => new Set()); // Set<client_id>
  // Thumbnail map: client_id → base64 jpeg data URL (populated by TemplateCard on mount)
  const thumbnailMapRef = useRef({});

  // Called by each card once the shared captureTemplateThumbnail util resolves.
  const handleThumbnailReady = useCallback((clientId, dataUrl) => {
    thumbnailMapRef.current[clientId] = dataUrl;
  }, []);

  // ── UI states ──────────────────────────────────────────────────────────────
  const [generating,  setGenerating]  = useState(false);
  const [savingAll,   setSavingAll]   = useState(false);
  const [pageError,   setPageError]   = useState('');
  const [statusMsg,   setStatusMsg]   = useState('');

  // ── Fetch category config on mount ────────────────────────────────────────
  useEffect(() => {
    console.log('[Generator] Fetching category config...');
    fetch(`${API_URL}/api/admin/content/generator/categories`, { headers: authH() })
      .then(r => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then(d => {
        const list = Array.isArray(d) ? d : [];
        console.log(`[Generator] Loaded ${list.length} categories`);
        setCategories(list);
      })
      .catch(e => {
        console.error('[Generator] Failed to load categories:', e.message);
        setPageError('Could not load categories. Check that the backend is running.');
      });
  }, []);

  const selectedCat = categories.find(c => c.id === category);
  const subcats     = selectedCat?.subcategories || [];
  const unsavedCount = previews.filter(p => !savedSet.has(p.client_id)).length;

  // ── Category change ────────────────────────────────────────────────────────
  const handleCategoryChange = (e) => {
    const val = e.target.value;
    console.log(`[Generator] Category selected: ${val}`);
    setCategory(val);
    setSubcategory('');   // always reset subcategory
    setPageError('');
  };

  const handleSubcategoryChange = (e) => {
    const val = e.target.value;
    console.log(`[Generator] Subcategory selected: ${val}`);
    setSubcategory(val);
    setPageError('');
  };

  // ── Generate previews ──────────────────────────────────────────────────────
  const handleGenerate = async () => {
    if (!category)    { setPageError('Please select a category.'); return; }
    if (!subcategory) { setPageError('Please select a subcategory.'); return; }

    const safeCount = Math.min(50, Math.max(1, count || 1));
    console.log(`[Generator] Generating ${safeCount} templates — category=${category} subcategory=${subcategory}`);

    setPageError('');
    setStatusMsg('');
    setGenerating(true);
    setSavedSet(new Set());        // clear saved state from previous run
    thumbnailMapRef.current = {};  // clear stale thumbnails
    setPreviews([]);

    try {
      const res = await fetch(`${API_URL}/api/admin/content/generator/preview`, {
        method:  'POST',
        headers: authH(),
        body:    JSON.stringify({ category, subcategory, count: safeCount, deduplicate }),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `Server error ${res.status}`);
      }

      const data  = await res.json();
      const items = (data.templates || []);

      console.log(`[Generator] Got ${items.length} templates`);
      // Validate each returned template has elements (debug guard)
      items.forEach((t, i) => {
        const elCount = t.template_json?.pages?.[0]?.elements?.length || 0;
        console.log(`[Generator]   template[${i}] client_id=${t.client_id} elements=${elCount}`);
      });

      setPreviews(items);
      setStatusMsg(`${items.length} template${items.length !== 1 ? 's' : ''} generated. Review below, then save.`);
    } catch (e) {
      console.error('[Generator] Generation failed:', e.message);
      setPageError(e.message || 'Generation failed — check backend logs.');
    } finally {
      setGenerating(false);
    }
  };

  // ── Save one template ──────────────────────────────────────────────────────
  // Returns a resolved promise on success, throws on failure.
  // Updates `savedSet` (state) which re-renders all cards + updates unsavedCount.
  const handleSaveOne = useCallback(async (item) => {
    const catLabel  = item.category?.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) || '';
    const subLabel  = item.subcategory?.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) || '';
    const hashPart  = (item.uniqueness_hash || item.template_json?.uniqueness_hash || '').slice(0, 6).toUpperCase();
    const name      = `${catLabel} ${subLabel}${hashPart ? ` — ${hashPart}` : ''}`.trim();

    const tj = item.template_json;

    // Client-side pre-check (belt + suspenders)
    const elements = tj?.pages?.[0]?.elements || [];
    if (elements.length === 0) {
      throw new Error('Template has no elements — blank canvas rejected');
    }

    const thumbnail = thumbnailMapRef.current[item.client_id] || null;
    console.log(`[Generator] Saving template "${name}" (${elements.length} elements) thumbnail=${!!thumbnail}`);

    const res = await fetch(`${API_URL}/api/admin/content/generator/save-one`, {
      method:  'POST',
      headers: authH(),
      body:    JSON.stringify({
        template_json: tj,
        name,
        category:     item.category,
        subcategory:  item.subcategory,
        auto_publish: autoPublish,
        tags:         [item.category, item.subcategory].filter(Boolean),
        thumbnail,
      }),
    });

    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || `Save failed (HTTP ${res.status})`);
    }

    const result = await res.json();
    console.log(`[Generator] Saved OK — db id=${result.id}`);

    // Update savedSet → triggers re-render → isSaved becomes true on that card
    setSavedSet(prev => {
      const next = new Set(prev);
      next.add(item.client_id);
      return next;
    });

    return result;
  }, [autoPublish]);

  // ── Regenerate one card ────────────────────────────────────────────────────
  const handleRegenOne = useCallback(async (clientId) => {
    console.log(`[Generator] Regenerating card ${clientId}`);
    try {
      const res = await fetch(`${API_URL}/api/admin/content/generator/preview`, {
        method:  'POST',
        headers: authH(),
        body:    JSON.stringify({ category, subcategory, count: 1, deduplicate: false }),
      });
      if (!res.ok) return;

      const data    = await res.json();
      const newItem = data.templates?.[0];
      if (!newItem) return;

      const elCount = newItem.template_json?.pages?.[0]?.elements?.length || 0;
      console.log(`[Generator] Regen card — new client_id=${newItem.client_id} elements=${elCount}`);

      // Replace old item with new one (new client_id → card unmounts/remounts → fresh local state)
      setPreviews(prev => prev.map(p => p.client_id === clientId ? newItem : p));

      // Remove stale thumbnail for the old card
      delete thumbnailMapRef.current[clientId];

      // Remove old clientId from savedSet if it was there
      setSavedSet(prev => {
        if (!prev.has(clientId)) return prev;
        const next = new Set(prev);
        next.delete(clientId);
        return next;
      });
    } catch (e) {
      console.error('[Generator] Regen failed:', e.message);
    }
  }, [category, subcategory]);

  // ── Delete one card ────────────────────────────────────────────────────────
  const handleDeleteOne = useCallback((clientId) => {
    setPreviews(prev => prev.filter(p => p.client_id !== clientId));
    setSavedSet(prev => {
      const next = new Set(prev);
      next.delete(clientId);
      return next;
    });
  }, []);

  // ── Save All ───────────────────────────────────────────────────────────────
  const handleSaveAll = async () => {
    const unsaved = previews.filter(p => !savedSet.has(p.client_id));
    if (!unsaved.length) { setStatusMsg('All templates are already saved.'); return; }

    setSavingAll(true);
    setPageError('');
    console.log(`[Generator] Save All — ${unsaved.length} unsaved templates`);

    const items = unsaved.map(item => {
      const catLabel = item.category?.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) || '';
      const subLabel = item.subcategory?.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) || '';
      const h        = (item.uniqueness_hash || '').slice(0, 6).toUpperCase();
      return {
        template_json: item.template_json,
        name:         `${catLabel} ${subLabel}${h ? ` — ${h}` : ''}`.trim(),
        category:     item.category,
        subcategory:  item.subcategory,
        auto_publish: autoPublish,
        tags:         [item.category, item.subcategory].filter(Boolean),
        thumbnail:    thumbnailMapRef.current[item.client_id] || null,
      };
    });

    try {
      const res = await fetch(`${API_URL}/api/admin/content/generator/save-batch`, {
        method:  'POST',
        headers: authH(),
        body:    JSON.stringify({ templates: items, auto_publish: autoPublish }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${res.status}`);
      }

      const data = await res.json();
      console.log(`[Generator] Save All done — saved=${data.saved} skipped=${data.skipped}`);

      // Mark all unsaved as saved
      setSavedSet(prev => {
        const next = new Set(prev);
        unsaved.forEach(p => next.add(p.client_id));
        return next;
      });

      const skipMsg = data.skipped > 0 ? ` (${data.skipped} skipped — validation failed)` : '';
      setStatusMsg(`Saved ${data.saved} template${data.saved !== 1 ? 's' : ''}${skipMsg}.`);

      if (data.errors?.length) {
        setPageError(data.errors.slice(0, 3).join(' | '));
      }
    } catch (e) {
      console.error('[Generator] Save All failed:', e.message);
      setPageError(e.message || 'Save All failed.');
    } finally {
      setSavingAll(false);
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <AdminLayout>
      <div className="max-w-7xl mx-auto space-y-6">

        {/* Header */}
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-violet-500 to-fuchsia-600 rounded-xl
              flex items-center justify-center shadow-lg shadow-violet-200 flex-shrink-0">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">AI Template Generator</h1>
              <p className="text-sm text-gray-500">Generate, preview, then save editable templates</p>
            </div>
          </div>

          <div className="flex items-center gap-2 text-xs text-amber-700 bg-amber-50
            border border-amber-200 rounded-xl px-3 py-2">
            <Info className="w-3.5 h-3.5 text-amber-500 flex-shrink-0" />
            <span>Templates are generated in memory. Review each one, then click <strong>Save</strong>.</span>
          </div>
        </div>

        {/* Settings panel */}
        <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm space-y-5">
          <h2 className="font-semibold text-gray-900 text-sm flex items-center gap-2">
            <Eye className="w-4 h-4 text-violet-500" />
            Generation Settings
          </h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">

            {/* Category */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Category <span className="text-red-400">*</span>
              </label>
              <DropDown
                value={category}
                onChange={handleCategoryChange}
                placeholder={categories.length ? 'Select category…' : 'Loading…'}
              >
                {categories.map(c => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </DropDown>
              {selectedCat && (
                <p className="text-[10px] text-gray-400 mt-1">
                  Canvas: {selectedCat.canvas.width}×{selectedCat.canvas.height}px
                </p>
              )}
            </div>

            {/* Subcategory */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Subcategory <span className="text-red-400">*</span>
              </label>
              <DropDown
                value={subcategory}
                onChange={handleSubcategoryChange}
                disabled={!category || subcats.length === 0}
                placeholder={
                  !category           ? 'Select category first'
                  : subcats.length === 0 ? 'No subcategories'
                  : 'Select subcategory…'
                }
              >
                {subcats.map(s => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </DropDown>
              {category && subcats.length > 0 && (
                <p className="text-[10px] text-gray-400 mt-1">
                  {subcats.length} subcategor{subcats.length !== 1 ? 'ies' : 'y'} available
                </p>
              )}
            </div>

            {/* Count */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Count <span className="text-gray-400 font-normal text-xs">(1–50)</span>
              </label>
              <input
                type="number"
                min={1} max={50}
                value={count}
                onChange={e => setCount(Math.min(50, Math.max(1, Number(e.target.value) || 1)))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm
                  focus:outline-none focus:ring-2 focus:ring-violet-500"
              />
            </div>

            {/* Options */}
            <div className="flex flex-col justify-end gap-3 pb-0.5">
              <Check
                checked={deduplicate}
                onChange={e => setDeduplicate(e.target.checked)}
                label="Avoid duplicate layouts"
              />
              <Check
                checked={autoPublish}
                onChange={e => setAutoPublish(e.target.checked)}
                label="Publish immediately on save"
              />
            </div>
          </div>

          {/* Status / error messages */}
          {pageError && (
            <div className="flex items-start gap-2 text-sm text-red-700 bg-red-50
              border border-red-200 rounded-xl px-4 py-3">
              <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span className="leading-snug">{pageError}</span>
            </div>
          )}
          {statusMsg && !pageError && (
            <div className="flex items-center gap-2 text-sm text-green-700 bg-green-50
              border border-green-200 rounded-xl px-4 py-2.5">
              <CheckCircle2 className="w-4 h-4 flex-shrink-0" />
              {statusMsg}
            </div>
          )}

          {/* Action row */}
          <div className="flex items-center gap-3 flex-wrap pt-1">
            <Button
              onClick={handleGenerate}
              disabled={generating || !category || !subcategory}
              className="bg-violet-600 hover:bg-violet-700 text-white gap-2 px-6 min-w-[190px]"
            >
              {generating ? (
                <><Loader2 className="w-4 h-4 animate-spin" /> Generating…</>
              ) : (
                <><Zap className="w-4 h-4" /> Generate {count} Template{count !== 1 ? 's' : ''}</>
              )}
            </Button>

            {unsavedCount > 0 && !generating && (
              <Button
                onClick={handleSaveAll}
                disabled={savingAll}
                variant="outline"
                className="border-violet-300 text-violet-700 hover:bg-violet-50 gap-2 px-5"
              >
                {savingAll ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /> Saving…</>
                ) : (
                  <><Save className="w-4 h-4" /> Save All ({unsavedCount})</>
                )}
              </Button>
            )}

            {previews.length > 0 && !generating && (
              <button
                onClick={() => {
                  setPreviews([]);
                  setSavedSet(new Set());
                  setStatusMsg('');
                  setPageError('');
                }}
                className="text-sm text-gray-400 hover:text-gray-600 transition-colors"
              >
                Clear all
              </button>
            )}
          </div>
        </div>

        {/* Generating spinner */}
        {generating && (
          <div className="flex items-center justify-center gap-3 py-20 text-gray-500">
            <Loader2 className="w-6 h-6 animate-spin text-violet-500" />
            <span>Generating {count} unique template{count !== 1 ? 's' : ''}…</span>
          </div>
        )}

        {/* Preview grid */}
        {!generating && previews.length > 0 && (
          <div>
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-semibold text-gray-900 flex items-center gap-2">
                <LayoutTemplate className="w-4 h-4 text-violet-500" />
                Previews
                <span className="text-sm font-normal text-gray-400">
                  ({previews.length} generated
                  {unsavedCount > 0 ? `, ${unsavedCount} unsaved` : ', all saved'})
                </span>
              </h2>
              <a
                href="/admin/templates"
                target="_blank"
                rel="noreferrer"
                className="text-sm text-violet-600 hover:text-violet-700 font-medium"
              >
                View in Templates →
              </a>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
              {previews.map(item => (
                <TemplateCard
                  key={item.client_id}
                  item={item}
                  isSaved={savedSet.has(item.client_id)}
                  onSave={handleSaveOne}
                  onDelete={handleDeleteOne}
                  onRegenerate={handleRegenOne}
                  onThumbnailReady={handleThumbnailReady}
                />
              ))}
            </div>
          </div>
        )}

        {/* Empty state */}
        {!generating && previews.length === 0 && (
          <div className="bg-white rounded-2xl border border-dashed border-gray-200 py-24 text-center">
            <Sparkles className="w-12 h-12 text-gray-200 mx-auto mb-4" />
            <p className="font-medium text-gray-400 text-lg">No previews yet</p>
            <p className="text-sm text-gray-300 mt-1 max-w-xs mx-auto">
              Choose a category + subcategory, set a count, then click Generate.
            </p>
          </div>
        )}

      </div>
    </AdminLayout>
  );
};

export default AdminTemplateGenerator;
